import React from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  LinearProgress,
  Drawer,
  Dialog,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import { CeleryStats, CeleryTask, CeleryWorker } from "../../api/celery";
import { ColorsEnum } from "../../common/theme/colors";
import TasksTable from "./TasksTable";
import TaskDetail from "../TaskDetail";
import * as S from "./style";

export type CancelTaskResult = {
  ok: boolean;
  message: string;
  statusCode?: number;
};

export type RerunTaskResult = {
  ok: boolean;
  newTaskId?: string | null;
  message: string;
};

export type CancelFeedback = {
  type: "success" | "error";
  message: string;
};

type TabValue =
  | "ALL"
  | "STARTED"
  | "SUCCESS"
  | "FAILURE"
  | "RETRY"
  | "PENDING"
  | "RECEIVED"
  | "REVOKED";

const TABS: TabValue[] = [
  "ALL",
  "STARTED",
  "SUCCESS",
  "FAILURE",
  "RETRY",
  "PENDING",
  "RECEIVED",
  "REVOKED",
];

interface CeleryPageProps {
  stats: CeleryStats | null;
  tasks: CeleryTask[];
  workers: Record<string, CeleryWorker>;
  loading: boolean;
  error: string | null;
  tab: TabValue;
  search: string;
  selectedTask: CeleryTask | null;
  drawerOpen: boolean;
  cancelInFlightTaskId: string | null;
  cancelFeedback: CancelFeedback | null;
  stateColours: Record<string, string>;
  onTabChange: (tab: TabValue) => void;
  onSearchChange: (search: string) => void;
  onRefresh: () => void;
  onOpenDetail: (task: CeleryTask) => void;
  onCloseDetail: () => void;
  onCancelTask: (taskId: string) => Promise<CancelTaskResult>;
  onRerunTask: (taskId: string) => Promise<RerunTaskResult>;
  rerunInFlightTaskId: string | null;
}

const CeleryPage: React.FC<CeleryPageProps> = ({
  stats,
  tasks,
  workers,
  loading,
  error,
  tab,
  search,
  selectedTask,
  drawerOpen,
  cancelInFlightTaskId,
  cancelFeedback,
  stateColours,
  onTabChange,
  onSearchChange,
  onRefresh,
  onOpenDetail,
  onCloseDetail,
  onCancelTask,
  onRerunTask,
  rerunInFlightTaskId,
}) => {
  if (loading && tasks.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const workerNames = Array.from(
    new Set([
      ...Object.keys(workers),
      ...tasks.map((t) => t.worker).filter((worker): worker is string => Boolean(worker)),
    ]),
  );
  const selectedTaskState = (selectedTask?.state || "").toUpperCase();
  const isRunningTask = selectedTaskState === "STARTED";

  return (
    <Box>
      {/* Header */}
      <Box sx={S.StyledHeaderBox}>
        <Typography variant="h5" fontWeight={700}>
          Celery Monitor
        </Typography>
        <Tooltip title="Refresh now">
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
          >
            Refresh
          </Button>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {cancelFeedback && (
        <Alert severity={cancelFeedback.type} sx={{ mb: 2 }}>
          {cancelFeedback.message}
        </Alert>
      )}

      {/* Stats Cards */}
      <Box sx={S.StyledStatsGrid}>
        <S.StyledStatsPaper elevation={1}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Total Tasks
          </Typography>
          <Typography variant="h4" fontWeight={600}>
            {stats?.total ?? "—"}
          </Typography>
        </S.StyledStatsPaper>
        {Object.entries(stats?.by_state || {}).map(([state, count]) => (
          <S.StyledStatsPaper
            key={state}
            elevation={1}
            onClick={() => onTabChange(state as TabValue)}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {state}
            </Typography>
            <Typography
              variant="h4"
              fontWeight={600}
              sx={{ color: stateColours[state] || ColorsEnum.grey }}
            >
              {count}
            </Typography>
          </S.StyledStatsPaper>
        ))}
        <S.StyledStatsPaper elevation={1}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Workers
          </Typography>
          <Typography variant="h4" fontWeight={600}>
            {workerNames.length}
          </Typography>
        </S.StyledStatsPaper>
      </Box>

      {/* Workers ribbon */}
      {workerNames.length > 0 && (
        <Box sx={S.StyledWorkersBox}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            Workers:
          </Typography>
          {workerNames.map((w) => (
            <Chip key={w} label={w} size="small" />
          ))}
        </Box>
      )}

      {/* Tabs + Search */}
      <Box sx={S.StyledTabsBox}>
        <Tabs
          value={tab}
          onChange={(_, v: TabValue) => onTabChange(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTab-root": { minWidth: "auto", textTransform: "none" },
          }}
        >
          {TABS.map((t) => (
            <Tab
              key={t}
              value={t}
              label={
                t === "ALL"
                  ? `All (${stats?.total ?? 0})`
                  : `${t} (${stats?.by_state?.[t] ?? 0})`
              }
            />
          ))}
        </Tabs>

        <TextField
          size="small"
          placeholder="Filter by task name…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{ minWidth: 220 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      {loading && <LinearProgress sx={{ mt: 1 }} />}

      {/* Tasks Table */}
      <TasksTable
        tasks={tasks}
        stateColours={stateColours}
        onTaskClick={onOpenDetail}
      />

      {/* Task Detail Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen && !isRunningTask}
        onClose={onCloseDetail}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 520 },
          },
        }}
      >
        {selectedTask && !isRunningTask && (
          <TaskDetail
            task={selectedTask}
            onClose={onCloseDetail}
            onCancelTask={onCancelTask}
            cancelLoading={cancelInFlightTaskId === selectedTask.id}
            onRerunTask={onRerunTask}
            rerunLoading={rerunInFlightTaskId === selectedTask.id}
          />
        )}
      </Drawer>

      <Dialog fullScreen open={drawerOpen && isRunningTask} onClose={onCloseDetail}>
        {selectedTask && isRunningTask && (
          <TaskDetail
            task={selectedTask}
            onClose={onCloseDetail}
            onBack={onCloseDetail}
            onCancelTask={onCancelTask}
            cancelLoading={cancelInFlightTaskId === selectedTask.id}
            onRerunTask={onRerunTask}
            rerunLoading={rerunInFlightTaskId === selectedTask.id}
          />
        )}
      </Dialog>
    </Box>
  );
};

export default CeleryPage;
